import React, { useState } from 'react';
import FieldTextValue from './FieldTextValue.jsx';
import { Modal } from '../ui-components/Modal.jsx';
import AddToCalendar from './AddToCalendar.jsx';
import ExpandableBio from '../ui-components/ExpandableBio.jsx';

import { Clock, MapPin, Mic, GraduationCap } from 'lucide-react';
import { getSessionSpeakerDisplayName, hasValidKey, isDisplayableRange } from '../utils/helpers.js';
import { sortSubsessions } from '../utils/sort-subsessions.js';
import SpeakerHeadshot from '../ui-components/SpeakerHeadshot.jsx';
import { FIXED_SESSION_FIELD_IDS } from '../constants.js';
import { useI18n } from '../i18n/index.jsx';
import SpeakerDisclosure from './SpeakerDisclosure.jsx';
import { groupSpeakersBySlug, roleGroupHeading } from '../utils/group-speakers-by-role.js';
import ceuCreditsLabel from '../utils/ceu-credits-label.js';

const SessionModal = ({
  visibleSpeakerFields = [],
  sessionData,
  showAddToCalendar = false,
  visibleSessionFields = [],
  visibleSessionFieldsById = {},
  onClose,
  onBack,
  onSpeakerClick,
  onSubsessionClick,
  primaryColor,
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const { t } = useI18n();

  // Helper function to extract field value from sessionData
  const getFieldValue = fieldId => {
    return sessionData[fieldId] || '-';
  };

  // Helper function to extract a raw array value for dot-separated rendering (e.g. tagsList for tags)
  const getFieldListValue = fieldId => {
    const list = sessionData[`${fieldId}List`];
    return Array.isArray(list) ? list.filter(Boolean) : null;
  };

  // Helper function to format field label with first letter uppercase
  const formatFieldLabel = fieldId => {
    return visibleSessionFieldsById[fieldId]?.name || fieldId.charAt(0).toUpperCase() + fieldId.slice(1) + ':';
  };

  /*
  const [expandedSubsessions, setExpandedSubsessions] = useState(new Set());

  const handleSubsessionToggle = subsessionId => {
    const newExpanded = new Set(expandedSubsessions);
    if (newExpanded.has(subsessionId)) {
      newExpanded.delete(subsessionId);
    } else {
      newExpanded.add(subsessionId);
    }
    setExpandedSubsessions(newExpanded);
  }; 
  */
  const customVisibleFields = visibleSessionFields.filter(fieldId => !FIXED_SESSION_FIELD_IDS.includes(fieldId));

  // SB-8853: slug-grouped participants with organizer labels.
  const speakerGroups = groupSpeakersBySlug(sessionData.speakers);

  return (
    <Modal
      onClose={onClose}
      onBack={onBack}
      className="session-modal"
      contentClassName="session-modal__content"
      style={primaryColor ? { '--color-primary': primaryColor } : {}}
    >
      {/* Session Header Info */}
      <div className="session-modal__session-header">
        <div className="session-modal__session-info">
          <h1 className="session-modal__title" style={{ color: primaryColor }}>
            {sessionData.title}
          </h1>

          <div className="session-modal__meta">
            {isDisplayableRange(sessionData?.date) &&
            (visibleSessionFields.includes('starts_at') || visibleSessionFields.includes('ends_at')) ? (
              <div className="session-modal__date">
                <Clock className="session-modal__icon" style={{ color: primaryColor }} />
                <span>{sessionData.date}</span>
              </div>
            ) : null}
            {hasValidKey(sessionData, 'location') && visibleSessionFields.includes('location') ? (
              <div className="session-modal__location">
                <MapPin className="session-modal__icon" style={{ color: primaryColor }} />
                <span>{sessionData.location}</span>
              </div>
            ) : null}
            {hasValidKey(sessionData, 'ceuCredits') && visibleSessionFields.includes('ceu_credits') ? (
              <div className="session-modal__location">
                <GraduationCap className="session-modal__icon" style={{ color: primaryColor }} />
                <span>
                  {ceuCreditsLabel([visibleSessionFieldsById], t)} {sessionData.ceuCredits}
                </span>
              </div>
            ) : null}
            <AddToCalendar sessionData={sessionData} enabled={showAddToCalendar} />
          </div>
        </div>
      </div>

      {/* Speakers Section — SB-8853: slug groups with organizer labels */}
      {visibleSessionFields.includes('speakers') && sessionData.speakers?.length > 0 && (
        <div className="session-modal__speakers">
          {speakerGroups.map(group => (
            <div key={group.slug} className="session-modal__speaker-group">
              <h2 className="session-modal__speakers-title">{roleGroupHeading(t, group)}</h2>
              <div className="session-modal__speakers-grid">
                {group.speakers.map(speaker => (
                  <div
                    key={`${speaker.id}-${group.slug}`}
                    className={`session-modal__speaker-card`}
                    onClick={() => onSpeakerClick?.(speaker.id)}
                  >
                    <SpeakerHeadshot speaker={speaker} />
                    <div className="session-modal__speaker-info">
                      <h3 className="session-modal__speaker-name">{getSessionSpeakerDisplayName(speaker, visibleSpeakerFields)}</h3>
                      {speaker.title && <p className="session-modal__speaker-title">{speaker.title}</p>}
                      {speaker.company && <p className="session-modal__speaker-company">{speaker.company}</p>}
                      <SpeakerDisclosure disclosure={speaker.disclosure} compact />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="session-modal__tabs" style={primaryColor ? { '--color-primary': primaryColor } : {}}>
        <div className="session-modal__tab-container">
          <button
            className={`session-modal__tab ${activeTab === 'details' ? 'session-modal__tab--active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            {t('session_details')}
          </button>
          <button
            className={`session-modal__tab ${activeTab === 'subsessions' ? 'session-modal__tab--active' : ''}`}
            onClick={() => setActiveTab('subsessions')}
            disabled={!sessionData.subsessions?.length}
          >
            {t('subsessions_tab', { count: sessionData.subsessions?.length || 0 })}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="session-modal__tab-content">
        {activeTab === 'details' && (
          <div className="session-modal__details">
            {/* Description */}
            {visibleSessionFields.includes('description') && sessionData.description ? (
              <div className="session-modal__description">
                <ExpandableBio
                  htmlContent={sessionData.description}
                  maxLength={300}
                  className="session-modal__description-content"
                />
              </div>
            ) : null}

            {/* Session Details */}
            {customVisibleFields.length > 0 && (
              <div className="session-modal__session-details">
                {customVisibleFields
                  .filter(fieldId => {
                    const value = sessionData[fieldId];
                    return value !== null && value !== undefined && value !== '';
                  })
                  .map(fieldId => {
                    const listValue = getFieldListValue(fieldId);
                    return (
                      <div key={fieldId} className="session-modal__detail-item">
                        <span className="session-modal__detail-label">{formatFieldLabel(fieldId)}</span>
                        {listValue ? (
                          <span className="session-modal__detail-value sb-dot-list">
                            {listValue.map((value, i) => (
                              <span key={i} className="sb-dot-list__item">
                                {value}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <FieldTextValue html={sessionData[`${fieldId}Html`]} className="session-modal__detail-value">
                            {getFieldValue(fieldId)}
                          </FieldTextValue>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'subsessions' && sessionData.subsessions && (
          <div className="session-modal__subsessions">
            {/* SB-8856: each row opens the subsession detail modal (pushed on
                the modal stack — Back/×/overlay pop one level). */}
            {sortSubsessions(sessionData.subsessions).map(subsession => (
              <div
                key={subsession.id}
                className={`session-modal__subsession-card ${
                  onSubsessionClick ? 'session-modal__subsession-card--clickable' : ''
                }`}
                onClick={onSubsessionClick ? () => onSubsessionClick(subsession.id) : undefined}
                role={onSubsessionClick ? 'button' : undefined}
                tabIndex={onSubsessionClick ? 0 : undefined}
                onKeyDown={
                  onSubsessionClick
                    ? event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSubsessionClick(subsession.id);
                        }
                      }
                    : undefined
                }
              >
                <div className="session-modal__subsession-header">
                  <h3 className="session-modal__subsession-title">{subsession.title}</h3>
                  <div className="session-modal__subsession-meta">
                    {isDisplayableRange(subsession.range) ? (
                      <div className="session-modal__subsession-date">
                        <Clock size={16} />
                        <span>{subsession.range}</span>
                      </div>
                    ) : null}
                    {subsession?.speakers?.length > 0 && (
                      <div className="session-modal__subsession-speakers">
                        <Mic size={16} />
                        <span className="sb-dot-list">
                          {(subsession.speakers || []).map((speaker, i) => (
                            <span key={speaker.id ?? i} className="sb-dot-list__item">
                              {speaker.full_name}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SessionModal;
